import React from "react";
import type {
  AnalyticsSnapshot,
  CharacterPerformance,
  SequencePerformance,
} from "../types";
import { SectionCard, StatCard } from "./SectionLayout";

interface AnalyticsSectionProps {
  overview: AnalyticsSnapshot;
  sequencePerformance: SequencePerformance[];
  characterPerformance: CharacterPerformance[];
}

const formatPercent = (value: number) =>
  `${(value * 100).toFixed(0)}%`;

export const AnalyticsSection: React.FC<AnalyticsSectionProps> = ({
  overview,
  sequencePerformance,
  characterPerformance,
}) => {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard
          label="Total Emails Sent"
          value={overview.totalEmailsSent.toLocaleString()}
          trendLabel={`${overview.weeklySends.toLocaleString()} this week`}
          accent="primary"
        />
        <StatCard
          label="Average Open Rate"
          value={formatPercent(overview.openRate)}
          accent="success"
        />
        <StatCard
          label="Average Click-through Rate"
          value={formatPercent(overview.clickRate)}
          accent="neutral"
        />
        <StatCard
          label="Replies Rate"
          value={formatPercent(overview.reengagementRate)}
          accent="success"
        />
      </div>

      <SectionCard
        title="Sequence Performance"
        description="Track how each follow-up performs across the funnel."
      >
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-widest text-gray-500">
              <tr className="text-left">
                <th className="px-4 py-3">Sequence Step</th>
                <th className="px-4 py-3">Open Rate</th>
                <th className="px-4 py-3">Click-through Rate</th>
                <th className="px-4 py-3">Replies Rate</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white text-gray-600">
              {sequencePerformance.map((step) => (
                <tr key={step.stepId}>
                  <td className="px-4 py-3 font-semibold text-gray-900">
                    {step.stepLabel}
                  </td>
                  <td className="px-4 py-3">{formatPercent(step.openRate)}</td>
                  <td className="px-4 py-3">{formatPercent(step.clickRate)}</td>
                  <td className="px-4 py-3">
                    {formatPercent(step.reengagementRate)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>

      <SectionCard
        title="Character Performance"
        description="Identify which characters bring users back most effectively."
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {characterPerformance.map((character) => (
            <div
              key={character.characterId}
              className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold text-gray-900">
                    {character.characterName}
                  </div>
                  <div className="text-xs text-gray-500">
                    {character.tier === "premium" ? "Premium tier" : "Free tier"}
                  </div>
                </div>
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  {character.sends} sends
                </div>
              </div>
              <div className="mt-4 space-y-2 text-sm">
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>Open rate</span>
                  <span className="font-semibold text-gray-900">
                    {formatPercent(character.openRate)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>Replies rate</span>
                  <span className="font-semibold text-gray-900">
                    {formatPercent(character.reengagementRate)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </SectionCard>
    </div>
  );
};
